import { ApartmentOutlined, CheckCircleFilled } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import {
  fetchAccessibleBranches,
  fetchSetupStatus,
  selectAccessibleBranches,
  selectSetupStatus
} from "../../../../Branches/redux/branches.slice.ts"
import { SettingsCard } from "../../common/SettingsCard.tsx"

const { Text } = Typography

/**
 * Company HQ only. "Active branches" is the live `accessibleBranches` count
 * (the same list Branches itself and the workspace selector read), never
 * `setupStatus.created` — that counter tracks the owner's onboarding plan
 * and can disagree with how many branches are active right now (one
 * archived since). Setup status is derived from those same two real
 * numbers rather than trusted as a separate flag, so the badge can never
 * read "complete" while the count above it still reads short of the plan.
 */
export function CompanyStructureCard() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const activeBranches = useAppSelector(selectAccessibleBranches)
  const setupStatus = useAppSelector(selectSetupStatus)

  useEffect(() => {
    dispatch(fetchAccessibleBranches())
    dispatch(fetchSetupStatus())
  }, [dispatch])

  const activeCount = activeBranches.length
  const plannedTotal = setupStatus?.target ?? null
  const isMultiBranchPlan = (plannedTotal ?? 0) > 1
  const isSetupComplete = isMultiBranchPlan ? activeCount >= (plannedTotal ?? 0) : true

  return (
    <SettingsCard
      iconWrapperClassName="bg-green-100"
      icon={<ApartmentOutlined className="text-green-600" />}
      title="Company structure"
    >
      <Flex vertical gap={"middle"}>
        <Flex justify="space-between" align="center" wrap gap={"small"}>
          <Flex vertical>
            <Text strong>
              {activeCount} active {activeCount === 1 ? "branch" : "branches"}
            </Text>
            <Text type="secondary" className="text-xs">
              {isMultiBranchPlan
                ? `Planned total: ${plannedTotal}`
                : "Single operation"}
            </Text>
          </Flex>
          {isMultiBranchPlan && (
            <Flex align="center" gap={"small"}>
              <CheckCircleFilled
                className={isSetupComplete ? "text-green-600" : "text-gray-400"}
              />
              <Flex vertical>
                <Text strong className={isSetupComplete ? "" : "text-gray-500"}>
                  {isSetupComplete ? "Setup complete" : "Setup in progress"}
                </Text>
                <Text type="secondary" className="text-xs">
                  {isSetupComplete
                    ? "All planned branches are active."
                    : `${activeCount} of ${plannedTotal} planned branches active.`}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>

        <Button block onClick={() => navigate(RoutesPaths.settings.branches)}>
          Manage branches
        </Button>

        <Text type="secondary" className="text-xs">
          HQ is not counted as a branch.
        </Text>
      </Flex>
    </SettingsCard>
  )
}
